import { ADVERSE_ANCHORS, ADVERSE_CONTEXTS } from './anchors'
import { SHARED_ANCHORS } from '../shared/anchors'
import { earsSteps, taskCardSteps } from '../shared/steps'
import { withScopes } from '../shared/scopes'
import type { IOnboardingScenario } from '../../types/IOnboardingScenario'
import type { IOnboardingScope } from '../../types/IOnboardingScope'
import type { IOnboardingStep } from '../../types/IOnboardingStep'

const { table, settings, form } = ADVERSE_ANCHORS
const { searchFilter } = SHARED_ANCHORS
const ctx = ADVERSE_CONTEXTS

/*
 * Области таблицы:
 * - actionsMenu — раскрытое «Меню действий» на мобильном (на десктопе действие ничего не делает);
 * - settingsMenu — открытое меню «Настройки»;
 * - typesModal, probabilityModal — модалки настроек видов НС и порогов вероятности;
 * - filterPanel — открытая панель «Поиск и фильтр».
 */
const tableScopes: Record<string, IOnboardingScope> = {
  actionsMenu: { open: { context: ctx.table, action: 'openActionsMenu' } },
  settingsMenu: {
    open: { context: ctx.table, action: 'openSettingsMenu' },
    close: { context: ctx.table, action: 'closeSettingsMenu' },
  },
  typesModal: {
    open: { context: ctx.table, action: 'openTypesModal' },
    close: { context: ctx.table, action: 'closeTypesModal' },
  },
  probabilityModal: {
    open: { context: ctx.table, action: 'openProbabilityModal' },
    close: { context: ctx.table, action: 'closeProbabilityModal' },
  },
  filterPanel: {
    open: { context: ctx.table, action: 'openFilterPanel' },
    close: { context: ctx.table, action: 'closeFilterPanel' },
  },
}

const tableSteps: IOnboardingStep[] = [
  { id: 'adverse.intro', anchors: [], context: ctx.table },
  { id: 'adverse.create', anchors: [table.create], context: ctx.table },
  ...withScopes(['actionsMenu'], [
    { id: 'adverse.statistics', anchors: [table.statistics], context: ctx.table },
    { id: 'adverse.reports', anchors: [table.reports], context: ctx.table },
  ]),
  ...withScopes(['actionsMenu', 'settingsMenu'], [
    { id: 'adverse.settings', anchors: [table.settings, table.settingsList], context: ctx.table, condition: 'adverse.canSettings', placement: 'left' },
  ]),
  { id: 'adverse.navSettings', anchors: [ADVERSE_ANCHORS.nav.settings], context: ctx.table, condition: 'adverse.canSettings', placement: 'right' },
  ...withScopes(['typesModal'], [
    { id: 'adverse.settings.types', anchors: [settings.types], context: ctx.table, condition: 'adverse.canEditTypes', placement: 'auto' },
    { id: 'adverse.settings.typesShowInForm', anchors: [settings.typesShowInForm], context: ctx.table, condition: 'adverse.canEditTypes' },
    { id: 'adverse.settings.typesView', anchors: [settings.typesView, settings.typesViewLast], context: ctx.table, condition: 'adverse.canEditTypes' },
    { id: 'adverse.settings.typesCreate', anchors: [settings.typesCreate], context: ctx.table, condition: 'adverse.canEditTypes', placement: 'left' },
  ]),
  ...withScopes(['probabilityModal'], [
    { id: 'adverse.settings.probability', anchors: [settings.probability], context: ctx.table, condition: 'adverse.canEditProbability', placement: 'auto' },
  ]),
  ...withScopes(['actionsMenu'], [
    { id: 'adverse.export', anchors: [table.export], context: ctx.table },
    { id: 'toolbar.reports', anchors: [SHARED_ANCHORS.toolbar.reports], context: ctx.table },
    { id: 'toolbar.onboarding', anchors: [SHARED_ANCHORS.toolbar.onboarding], context: ctx.table },
  ]),
  { id: 'adverse.search', anchors: [searchFilter.field], context: ctx.table, placement: 'bottom' },
  { id: 'adverse.search.toggle', anchors: [searchFilter.toggle], context: ctx.table, placement: 'bottom' },
  ...withScopes(['filterPanel'], [
    { id: 'adverse.filter.presets', anchors: [searchFilter.presets], context: ctx.table, placement: 'left' },
    { id: 'adverse.filter.words', anchors: [searchFilter.words], context: ctx.table, placement: 'left' },
    { id: 'adverse.filter.add', anchors: [searchFilter.add], context: ctx.table, placement: 'left' },
    { id: 'adverse.filter.apply', anchors: [searchFilter.footer], context: ctx.table, placement: 'top' },
  ]),
  { id: 'adverse.filter.chips', anchors: [searchFilter.chips], context: ctx.table, placement: 'bottom' },
  { id: 'adverse.filters', anchors: [table.filterFirst, table.filterLast], context: ctx.table },
  { id: 'adverse.resetFilters', anchors: [table.resetFilters], context: ctx.table },
  { id: 'adverse.hideCompleted', anchors: [table.hideCompleted], context: ctx.table },
  { id: 'adverse.demoRow', anchors: [table.demoRow], context: ctx.table },
]

/* Области формы одинаковы в обоих сценариях, отличается только то, как открывается сама форма */
const formSteps: IOnboardingStep[] = [
  ...withScopes(['form'], [
    { id: 'adverse.form.type', anchors: [form.type], context: ctx.form },
    { id: 'adverse.form.dateTime', anchors: [form.dateTime], context: ctx.form },
    { id: 'adverse.form.risk', anchors: [form.risk], context: ctx.form },
    { id: 'adverse.form.place', anchors: [form.department, form.location], context: ctx.form },
    { id: 'adverse.form.participants', anchors: [form.participants], context: ctx.form },
    { id: 'adverse.form.description', anchors: [form.description], context: ctx.form },
    { id: 'adverse.form.corrective', anchors: [form.corrective], context: ctx.form },
    { id: 'adverse.form.save', anchors: [form.save], context: ctx.form },
  ]),
  ...withScopes(['form', 'questionnairesTab'], [
    { id: 'adverse.form.questionnaires', anchors: [form.questionnairesTab, form.questionnaires], context: ctx.form, placement: 'bottom' },
  ]),
  ...withScopes(['form', 'filesTab'], [
    { id: 'adverse.form.files', anchors: [form.filesTab, form.files], context: ctx.form, placement: 'bottom' },
  ]),
  ...withScopes(['form'], [
    { id: 'adverse.form.historyTab', anchors: [form.historyTab], context: ctx.form },
    { id: 'adverse.form.tasksEar', anchors: [SHARED_ANCHORS.ear.tasks], context: ctx.form, placement: 'left' },
  ]),
  ...withScopes(['form', 'tasksPane'], [
    { id: 'adverse.form.assignCoordinator', anchors: [form.assignCoordinator], context: ctx.formTasks, placement: 'left' },
    ...taskCardSteps(ctx.formTasks),
  ]),
  ...earsSteps(ctx.form, ['form']),
]

const formTabScopes: Record<string, IOnboardingScope> = {
  questionnairesTab: {
    open: { context: ctx.form, action: 'openQuestionnairesTab' },
    close: { context: ctx.form, action: 'openFormTab' },
  },
  filesTab: {
    open: { context: ctx.form, action: 'openFilesTab' },
    close: { context: ctx.form, action: 'openFormTab' },
  },
  tasksPane: {
    open: { context: ctx.formTasks, action: 'openTasksPane' },
    close: { context: ctx.formTasks, action: 'closeTasksPane' },
  },
  chatPane: {
    open: { context: ctx.form, action: 'openDemoChat' },
    close: { context: ctx.form, action: 'closeDemoChat' },
  },
}

export const adverseScenarios: IOnboardingScenario[] = [
  {
    id: 'adverseEvents',
    section: 'adverse_event',
    isMain: true,
    scopes: {
      ...tableScopes,
      form: {
        open: { context: ctx.table, action: 'openDemoForm' },
        close: { context: ctx.table, action: 'closeForm' },
      },
      ...formTabScopes,
    },
    steps: [...tableSteps, ...formSteps],
  },
  {
    id: 'adverseEvents.form',
    section: 'adverse_event',
    isMain: false,
    scopes: {
      form: {
        open: { context: ctx.form, action: 'showDemo' },
        close: { context: ctx.form, action: 'showReal' },
      },
      ...formTabScopes,
    },
    steps: formSteps,
  },
]
